import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Radio, Loader2, ChevronRight, CheckCircle2, AlertCircle, XCircle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sync & Select — combines the automation of Sync Now with the review-first
 * UX of Quick Log. Admin fetches recent Telegram messages, picks the daily
 * performance post from the list, and routes it into the Quick Log modal
 * for final review + save (no copy-paste required).
 */
export default function SyncSelectModal({ open, onClose, ib, onPickMessage }) {
  const [channelType, setChannelType] = useState('');
  const [limit, setLimit] = useState(50);
  const [sinceDate, setSinceDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState(null);
  // Text search within fetched candidates — lets admin find their daily
  // performance post by keyword (e.g., "Gold" or "Total Win") even when
  // the channel is very busy and the post is buried among promos.
  const [searchText, setSearchText] = useState('');

  const token = localStorage.getItem('token');
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const runPreview = async () => {
    if (!ib?.id) return;
    setLoading(true);
    setCandidates(null);
    try {
      const res = await axios.post(`${API}/operations/template/preview-from-telegram`, {
        ib_id: ib.id,
        channel_type: channelType || undefined,
        limit,
        since_date: sinceDate || undefined,
      }, h);
      if (res.data?.success) {
        setCandidates(res.data.candidates || []);
        if ((res.data.candidates || []).length === 0) {
          toast.info('Tiada mesej dijumpai dalam range yang dipilih');
        } else {
          const withTrades = (res.data.candidates || []).filter(c => c.trades_count > 0).length;
          toast.success(`Dijumpai ${res.data.count} mesej (${withTrades} dengan trades)`);
        }
      } else {
        toast.error(res.data?.error || 'Preview gagal');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Preview gagal';
      toast.error(typeof msg === 'string' ? msg : 'Preview gagal');
    } finally { setLoading(false); }
  };

  const pick = (candidate) => {
    onPickMessage?.(candidate.text);
    onClose();
    setCandidates(null);
    setSinceDate('');
  };

  // Sort candidates: those with trades first (they're what admin actually wants),
  // then by date desc within each group. Applies optional text search filter.
  const sortedCandidates = useMemo(() => {
    if (!candidates) return [];
    const q = searchText.trim().toLowerCase();
    let list = candidates;
    if (q) {
      list = candidates.filter(c => (c.text || '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (a.trades_count > 0 && b.trades_count === 0) return -1;
      if (a.trades_count === 0 && b.trades_count > 0) return 1;
      return (b.posted_at_iso || '').localeCompare(a.posted_at_iso || '');
    });
  }, [candidates, searchText]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setCandidates(null); } }}>
      <DialogContent className="max-w-3xl bg-[hsl(var(--card))] border-[hsl(var(--border))] max-h-[90vh] overflow-y-auto" data-testid="sync-select-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <Sparkles size={16} /> Sync &amp; Select — Auto-fetch dari Telegram, pilih, save
          </DialogTitle>
          <DialogDescription className="text-xs">
            Scan mesej Telegram terkini tanpa auto-save. Pilih daily performance post → auto-open Quick Log untuk review + save.
          </DialogDescription>
        </DialogHeader>

        {/* Filter controls */}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Channel</label>
            <Select value={channelType || 'all'} onValueChange={(v) => setChannelType(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="sync-select-channel"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <SelectItem value="all">All linked</SelectItem>
                <SelectItem value="public">Public only</SelectItem>
                <SelectItem value="vip">VIP only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Messages to scan</label>
            <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
              <SelectTrigger className="h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="sync-select-limit"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <SelectItem value="30">Last 30</SelectItem>
                <SelectItem value="50">Last 50</SelectItem>
                <SelectItem value="100">Last 100</SelectItem>
                {sinceDate && <SelectItem value="200">Last 200 (deep)</SelectItem>}
                {sinceDate && <SelectItem value="500">Last 500 (deepest)</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Since date (optional)</label>
            <input
              type="date"
              value={sinceDate}
              onChange={(e) => setSinceDate(e.target.value)}
              data-testid="sync-select-since-date"
              className="h-9 text-xs bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded px-2 text-white mt-1 w-full"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="border-[hsl(var(--border))]">Cancel</Button>
          <Button onClick={runPreview} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="sync-select-fetch-btn">
            {loading ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Radio size={12} className="mr-1.5" />}
            Fetch Messages
          </Button>
        </div>

        {/* Linked channels indicator — helps admin verify they're scanning
            the RIGHT channels. Common reason for "can't find my post": the
            channel where daily performance is posted isn't linked to this IB. */}
        {candidates === null && !loading && (
          <div className="mt-3 p-3 rounded-lg border border-blue-500/20 bg-blue-500/5 text-[11px]">
            <p className="font-bold text-blue-300 mb-1">Sebelum sync, sahkan:</p>
            <ol className="list-decimal list-inside space-y-1 text-[hsl(var(--text-muted))]">
              <li>Channel Telegram yang tuan post daily performance MESTI dah di-link ke IB ni. Semak di <strong className="text-white">Admin → Integration → Telegram Channels</strong>.</li>
              <li>Nama channel dalam Admin tak semestinya sama dengan nama channel Telegram sebenar. Yang penting: <strong className="text-white">chat_id</strong> betul.</li>
              <li>Kalau posting agak lama (contoh 3-4 hari lepas), set <strong className="text-white">Since date</strong> + limit 200-500.</li>
            </ol>
          </div>
        )}
        {/* Candidate list */}
        {candidates !== null && !loading && (
          <div className="mt-4 space-y-2 border-t border-[hsl(var(--border))] pt-3">
            {sortedCandidates.length === 0 ? (
              <div className="text-center py-6 text-sm text-[hsl(var(--text-muted))] space-y-2">
                <p>Tiada mesej dijumpai dalam range yang dipilih.</p>
                <p className="text-[11px] text-amber-300">
                  ⚠ Kemungkinan besar: <strong>channel Telegram tuan belum di-link ke IB ni</strong>.
                  <br />Buka <strong>Admin → Integration</strong> dan tambah chat_id channel yang tuan post daily performance.
                </p>
                <p className="text-[10px]">Kalau channel dah linked, cuba increase limit ke 200-500 dengan since_date lebih awal.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 text-[11px] text-[hsl(var(--text-muted))]">
                  <span>{candidates.length} mesej dijumpai · showing {sortedCandidates.length} · yang ada trades ditunjukkan dulu ↓</span>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search text (contoh: Gold, Total Win)..."
                    className="h-7 text-xs bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded px-2 text-white w-56"
                    data-testid="sync-candidate-search"
                  />
                </div>
                {sortedCandidates.map((c) => {
                  const hasTrades = c.trades_count > 0;
                  return (
                    <div
                      key={`${c.chat_id}-${c.message_id}`}
                      data-testid={`sync-candidate-${c.message_id}`}
                      className={`rounded-lg border p-3 ${hasTrades ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))]'}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="font-mono text-cyan-400">#{c.message_id}</span>
                          <span className="text-white font-medium">{c.chat_name}</span>
                          <span className="text-[hsl(var(--text-muted))]">·</span>
                          <span className="text-[hsl(var(--text-muted))]">{c.posted_date || '—'}</span>
                          {c.channel_type && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300 uppercase">
                              {c.channel_type}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {hasTrades ? (
                            <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-400">
                              <CheckCircle2 size={12} />
                              {c.trades_count} trade{c.trades_count > 1 ? 's' : ''}
                              {Number.isFinite(c.computed_total) && (
                                <span className="text-emerald-300 font-normal ml-1">
                                  ({c.computed_total > 0 ? '+' : ''}{c.computed_total} PIP)
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[11px] text-[hsl(var(--text-muted))]">
                              <XCircle size={12} />
                              Not template
                            </span>
                          )}
                          <Button
                            size="sm"
                            onClick={() => pick(c)}
                            data-testid={`sync-candidate-use-${c.message_id}`}
                            className={hasTrades ? 'bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-[11px]' : 'bg-slate-500 hover:bg-slate-600 text-white h-7 text-[11px]'}
                          >
                            Use This <ChevronRight size={11} className="ml-0.5" />
                          </Button>
                        </div>
                      </div>
                      <pre className="text-[11px] text-[hsl(var(--text))] bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded p-2 whitespace-pre-wrap break-words font-mono max-h-28 overflow-y-auto">
{c.text_preview}
                      </pre>
                      {hasTrades && c.stated_total != null && !c.totals_match && (
                        <div className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-400">
                          <AlertCircle size={10} />
                          Stated total ({c.stated_total}) tak sama dengan computed ({c.computed_total}) — semak dalam Quick Log
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
